import { useState } from "react";
import styles from "../../../../components/TemplateSelectionScreen/TemplateSelectionScreen.module.css";
import { Badge, Button, Card, Icon, RadioButton, Tooltip } from "@shopify/polaris";
import { ArrowLeftIcon, InfoIcon } from "@shopify/polaris-icons";
import { Link } from "react-router-dom";
import {
    BBTemplateInfo,
    BBTemplateInfoRightFooter,
} from "../../../../components/TemplateSelectionScreen/TemplateSelectionConstants";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import { LazyImageWithSkeleton } from "../../../../components/TemplateSelectionScreen/LazyImageWithSkeleton";
import { AvailableBundleBuilderTemplates } from "../../constants";
import { useFormContext, useWatch } from "react-hook-form";
import { BundleBuilderConfigInterface } from "../../Types";

interface TemplateSelectionScreenBBProps {
    makeClean: () => void;
}

const TemplateSelectionScreenBB = ({ makeClean }: TemplateSelectionScreenBBProps) => {
    const { control, setValue, getValues } = useFormContext<BundleBuilderConfigInterface>();
    const selectedTemplateBb = useWatch({ control, name: "selected_template_bb" });
    const footerLayout = useWatch({ control, name: "footer_layout" });

    const handleChangeSelectedTemplate = (template: string) => {
        let discount_type = "";
        let show_progress_bar = false;
        switch (template) {
            case AvailableBundleBuilderTemplates.FULL_CARD_INFO:
                discount_type = "tier";
                show_progress_bar = true;
                break;
            case AvailableBundleBuilderTemplates.BASIC_CARD_INFO:
                discount_type = "fixed_boxes";
                break;
            case AvailableBundleBuilderTemplates.IMG_FOCUS_CARD:
                discount_type = "fixed";
                break;
            case AvailableBundleBuilderTemplates.HOVER_ACTION_CARD:
                discount_type = "percentage";
                break;
            case AvailableBundleBuilderTemplates.HOVER_INFO_CARD:
                discount_type = "fixed";
                break;
            default:
                discount_type = "percentage";
                break;
        }

        const isMinimalTemplate = template === AvailableBundleBuilderTemplates.BASIC_CARD_INFO;
        const defaultFixedBoxes = isMinimalTemplate
            ? [{ itemsCount: 2, fixedPrice: 50, boxName: "Box of 2" }]
            : getValues("fixed_boxes") || [];

        setValue("discount_type" as any, discount_type, { shouldDirty: true });
        setValue("show_progress_bar" as any, show_progress_bar, { shouldDirty: true });
        setValue("selected_template_bb" as any, template, { shouldDirty: true });
        if (isMinimalTemplate) setValue("hide_bundle_header" as any, true, { shouldDirty: true });
        setValue("fixed_boxes" as any, defaultFixedBoxes, { shouldDirty: true });
        makeClean();
    };

    const handleChangeSelectedFooterLayout = (template: string) => {
        setValue("footer_layout" as any, template, { shouldDirty: true });
        if (template === "right") {
            const currentReviewStep = getValues("review_step");
            setValue(
                "review_step" as any,
                {
                    ...currentReviewStep,
                    step_label: "Review Your Box",
                    page_title: "You've made the right choice!",
                    is_enabled: false,
                },
                { shouldDirty: true },
            );
        }
    };

    const [openPreviewModal, setOpenPreviewModal] = useState(false);
    const [imgType, setImgType] = useState("bottom");

    const templates = footerLayout === "right" ? BBTemplateInfoRightFooter : BBTemplateInfo;

    return (
        <div
            className={styles.templateSelectionWrapper}
            style={{
                width: "94%",
            }}
        >
            <h1 className={styles.templateSelectionHeading}>
                <Link to={"/"}>
                    <ArrowLeftIcon width={18} className={styles.templateSelectionBackButton} />
                </Link>
                Choose a template
            </h1>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                }}
            >
                <RadioButton
                    label={
                        <div style={{ display: "flex", gap: "10px" }}>
                            <p>Footer Layout</p>
                            <Button
                                variant="plain"
                                onClick={() => {
                                    setOpenPreviewModal(true);
                                    setImgType("bottom");
                                }}
                            >
                                Preview
                            </Button>
                        </div>
                    }
                    id={`footer_layout`}
                    key={`fl`}
                    checked={footerLayout == "bottom"}
                    onChange={() => {
                        handleChangeSelectedFooterLayout("bottom");
                    }}
                />
                <RadioButton
                    label={
                        <div style={{ display: "flex", gap: "10px" }}>
                            <p>Sidebar Layout</p>
                            <Button
                                variant="plain"
                                onClick={() => {
                                    setOpenPreviewModal(true);
                                    setImgType("right");
                                }}
                            >
                                Preview
                            </Button>
                        </div>
                    }
                    id={`sidebar_layout`}
                    key={`sl`}
                    checked={footerLayout == "right"}
                    onChange={() => {
                        handleChangeSelectedFooterLayout("right");
                    }}
                />
            </div>

            {footerLayout && (
                <Card>
                    <div id="templateSelectionBB" className={styles.templateSelectionSubHeading}>
                        Select a template to continue
                    </div>
                    <div className={`${styles.templateSelectionGrid} ${styles.custom}`}>
                        {templates.map((_t: any) => (
                            <div key={_t.template} className={styles.templateSelectionCard}>
                                {_t.icon}

                                <div className={styles.templateSelectionCardBottomRow}>
                                    <div className={styles.templateSelectionCardLabelRow}>
                                        <div>
                                            <span className={styles.cardHeading}>{_t.label}</span>
                                            {_t.desc && (
                                                <div className={styles.descriptionContainer}>
                                                    <p className={styles.desc}>{_t.desc}</p>
                                                </div>
                                            )}
                                        </div>
                                        {_t.hoverText && (
                                            <Tooltip content={_t.hoverText}>
                                                <Icon source={InfoIcon} tone="base" />
                                            </Tooltip>
                                        )}
                                    </div>
                                    <div>
                                        <Button
                                            variant="secondary"
                                            onClick={
                                                ((e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    handleChangeSelectedTemplate(_t.template);
                                                }) as unknown as () => void
                                            }
                                        >
                                            {selectedTemplateBb === _t.template ? "Selected" : "Select"}
                                        </Button>
                                    </div>
                                </div>

                                <div className={styles.contentArea}>
                                    {_t.tags && _t.tags.length > 0 && (
                                        <div className={styles.tagsContainer}>
                                            <span className={styles.label}>Ideal For: </span>
                                            {_t.tags.map((tag: string, index: number) => (
                                                <p key={index}>
                                                    <Badge tone="success">{tag}</Badge>
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {openPreviewModal && (
                <CustomModal
                    title={"Preview"}
                    open={openPreviewModal}
                    onClose={() => {
                        setOpenPreviewModal(false);
                    }}
                    noScroll
                >
                    <div>
                        <LazyImageWithSkeleton
                            src={imgType == "bottom" ? "/svgs/BBFooterTemplate.svg" : "/svgs/BBSideBarTemplate.svg"}
                            alt="BB"
                            maxHeight={400}
                            objectFit="contain"
                        />
                    </div>
                </CustomModal>
            )}
        </div>
    );
};

export default TemplateSelectionScreenBB;
